// CercleDigital InsiderGems, gestion électronique documentaire partagée de l'écosystème Cercle.
// Copyright 2026 Pierre-Thibault Fabre <pierre-thibault@cercle.digital>
// SPDX-License-Identifier: LicenseRef-Cercle-Interne
//
// Fichier : apps/web/src/lib/glissement.ts
// Rôle : détecte les glissements de bord, pour ouvrir les tiroirs sur mobile.
// Référence : CIG-SPECIFICATION.md §5.2 point 1, §5.4 point 5.

import { onMounted, onUnmounted } from 'vue';

export interface OptionsGlissement {
  /** Largeur de la zone de bord sensible, en pixels. */
  bord?: number;
  /** Distance minimale à parcourir pour valider le geste. */
  distance?: number;
  /** Écart vertical au-delà duquel le geste est tenu pour un défilement. */
  tolerance?: number;
  onVersDroite?: () => void;
  onVersGauche?: () => void;
  /** Vrai si le geste doit être ignoré, par exemple pendant une modale. */
  inactif?: () => boolean;
}

/**
 * Sur écran étroit, les barres latérales sont repliées et s'ouvrent au
 * glissement depuis le bord. Le geste ne part que du bord : ailleurs, il
 * entrerait en concurrence avec le défilement et la sélection au rectangle.
 */
export function useGlissementDeBord(opts: OptionsGlissement): void {
  const bord = opts.bord ?? 24;
  const distance = opts.distance ?? 60;
  const tolerance = opts.tolerance ?? 45;

  let depart: { x: number; y: number; depuisBordGauche: boolean; depuisBordDroit: boolean } | null = null;

  function debut(ev: TouchEvent): void {
    if (opts.inactif?.()) return;
    if (ev.touches.length !== 1) return;
    const t = ev.touches[0];
    depart = {
      x: t.clientX,
      y: t.clientY,
      depuisBordGauche: t.clientX <= bord,
      depuisBordDroit: t.clientX >= window.innerWidth - bord,
    };
  }

  function fin(ev: TouchEvent): void {
    const d = depart;
    depart = null;
    if (!d) return;
    const t = ev.changedTouches[0];
    if (!t) return;

    const dx = t.clientX - d.x;
    const dy = Math.abs(t.clientY - d.y);
    // Un geste trop vertical relève du défilement, pas de l'ouverture.
    if (dy > tolerance || Math.abs(dx) < distance) return;

    if (dx > 0 && d.depuisBordGauche) opts.onVersDroite?.();
    else if (dx < 0 && d.depuisBordDroit) opts.onVersGauche?.();
    // Un glissement vers l'intérieur depuis n'importe où referme le tiroir
    // ouvert : le geste de fermeture n'a pas à partir du bord.
    else if (dx < 0) opts.onVersGauche?.();
    else opts.onVersDroite?.();
  }

  onMounted(() => {
    window.addEventListener('touchstart', debut, { passive: true });
    window.addEventListener('touchend', fin, { passive: true });
  });
  onUnmounted(() => {
    window.removeEventListener('touchstart', debut);
    window.removeEventListener('touchend', fin);
  });
}

/** Vrai si la fenêtre est en dessous du seuil de disposition en colonnes. */
export function estEtroit(seuil = 60 * 16): boolean {
  return window.innerWidth < seuil;
}
